import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AnimalsPage = lazy(() => import('./pages/AnimalsPage'));
const AnimalDetailPage = lazy(() => import('./pages/AnimalDetailPage'));
const CreateAnimalPage = lazy(() => import('./pages/CreateAnimalPage'));
const RanchesPage = lazy(() => import('./pages/RanchesPage'));
const RanchDetailPage = lazy(() => import('./pages/RanchDetailPage'));
const CreateRanchPage = lazy(() => import('./pages/CreateRanchPage'));
const CertificationVerificationPage = lazy(() => import('./pages/CertificationVerificationPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const CreateUserPage = lazy(() => import('./pages/CreateUserPage'));
const EditUserPage = lazy(() => import('./pages/EditUserPage'));
const ImportsPage = lazy(() => import('./pages/ImportsPage'));
const ImportDetailPage = lazy(() => import('./pages/ImportDetailPage'));
const MobilizationsPage = lazy(() => import('./pages/MobilizationsPage'));
const DataTablesPage = lazy(() => import('./pages/DataTablesPage'));
const ApiKeyManagementPage = lazy(() => import('./pages/ApiKeyManagementPage'));
const CreateApiKeyPage = lazy(() => import('./pages/CreateApiKeyPage'));
const EditApiKeyPage = lazy(() => import('./pages/EditApiKeyPage'));

function App() {
  const { user, loading, isDataManager, isSlaughterhouseUser } = useAuth();
  const navigate = useNavigate();
  
  // Mostrar pantalla de carga solo durante la autenticación inicial
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
        <p className="ml-4 text-gray-600">
          Verificando autenticación...
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      {/* <StagewiseToolbar 
        config={{
          plugins: [ReactPlugin]
        }} 
      /> */}
      <Layout>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
          {/* Redirección inicial según rol */}
          <Route path="/" element={
            isSlaughterhouseUser ? 
              <Navigate to="/certification" replace /> : 
              <Navigate to="/dashboard" replace />
          } />
          
          {/* Rutas solo para Data Managers */}
          <Route path="/dashboard" element={
            isDataManager ? <Dashboard /> : <Navigate to="/certification" replace />
          } />
          <Route path="/animals" element={
            isDataManager ? <AnimalsPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/animals/new" element={
            isDataManager ? <CreateAnimalPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/animals/:din" element={
            isDataManager ? <AnimalDetailPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/ranches" element={
            isDataManager ? <RanchesPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/ranches/new" element={
            isDataManager ? <CreateRanchPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/ranches/:id" element={
            isDataManager ? <RanchDetailPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/users" element={
            isDataManager ? <UserManagementPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/users/create" element={
            isDataManager ? <CreateUserPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/users/edit/:uid" element={
            isDataManager ? <EditUserPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/imports" element={
            isDataManager ? <ImportsPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/imports/:id" element={
            isDataManager ? <ImportDetailPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/mobilizations" element={
            isDataManager ? <MobilizationsPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/data-tables" element={
            isDataManager ? <DataTablesPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/management/api-keys" element={
            isDataManager ? <ApiKeyManagementPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/management/api-keys/new" element={
            isDataManager ? <CreateApiKeyPage /> : <Navigate to="/certification" replace />
          } />
          <Route path="/management/api-keys/edit/:id" element={
            isDataManager ? <EditApiKeyPage /> : <Navigate to="/certification" replace />
          } />
          
          {/* Ruta accesible para ambos roles */}
          <Route path="/certification" element={<CertificationVerificationPage />} />
          
          {/* Redirecciones */}
          <Route path="/login" element={
            isSlaughterhouseUser ? 
              <Navigate to="/certification" replace /> : 
              <Navigate to="/dashboard" replace />
          } />
          
          {/* 404 - Redirigir según rol */}
          <Route path="*" element={
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Página no encontrada</h1>
              <p className="text-gray-600 mb-6">La ruta que buscas no existe.</p>
              <button 
                onClick={() => navigate(isSlaughterhouseUser ? '/certification' : '/dashboard')} 
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                {isSlaughterhouseUser ? 'Volver a Certificación' : 'Volver al Dashboard'}
              </button>
            </div>
          } />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
}

export default App;